import React, { useState } from 'react';
import CaseApplicantTable from 'app/edozvola/cases/case-applicant/case-applicant-table';
import CaseApplicantForm from 'app/edozvola/cases/case-applicant/case-applicant-form';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  copyApplicantsFromCaseQuery,
  fetchApplicantsNumberQuery,
  saveApplicantsNumberQuery,
} from 'app/edozvola/cases/case-applicant/service/case-applicant.queries';
import { useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import {
  applicantsNumberSchema,
  caseApplicantCaseUuidToCopyFromSchema,
} from 'app/edozvola/cases/case-applicant/model/case-applicant.schema';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, Row } from 'reactstrap';
import { fetchApplicantsNumberCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { useDispatch } from 'react-redux';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { CASE_TAB_ENUM } from 'app/config/constants';
import CaseApplicantFormWithNonMandatoryOib from 'app/edozvola/cases/case-applicant/case-applicant-form-with-non-mandatory-oib';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseApplicant = ({ loaderData = null }) => {
  const { id: uuid } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [caseApplicantId, setCaseApplicantId] = useState<string | null>(null);
  const [systemRecorded, setSystemRecorded] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const { sidebarItemPair } = getCaseContext();

  const { data: applicantsNumber, isLoading: applicantsNumberLoading } = fetchApplicantsNumberQuery(uuid);
  const { data: applicantsNumberOptions, isLoading: applicantsNumberOptionsLoading } = fetchApplicantsNumberCodebookQuery();

  const applicantsVariant = loaderData;

  const copyApplicantsFormik = useFormik({
    initialValues: { caseUuidToCopyFrom: '' },
    validationSchema: caseApplicantCaseUuidToCopyFromSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: values => copyApplicants(values.caseUuidToCopyFrom),
  });

  const applicantsNumberFormik = useFormik({
    initialValues: { applicantsNumber: applicantsNumber ?? [] },
    validationSchema: applicantsNumberSchema,
    onSubmit: values => saveApplicantsNumber(values.applicantsNumber),
    initialTouched: { applicantsNumber: true },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const { mutate: copyApplicants, isPending: isCopyApplicantsPending } = copyApplicantsFromCaseQuery(uuid, copyApplicantsFormik.resetForm);
  const { mutate: saveApplicantsNumber, isPending: applicantsNumberSaving } = saveApplicantsNumberQuery(
    uuid,
    applicantsNumberFormik.resetForm,
  );

  const openNewApplicantForm = () => {
    setCaseApplicantId('');
    setReadOnly(false);
    setSystemRecorded(false);
  };

  return (
    <>
      <FormikProvider value={applicantsNumberFormik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <div className={'form-header'}>Podnositelji zahtjeva i opunomoćenici</div>
        <div className={'mt-3 pb-4'}>
          <Row>
            <Col md={5}>
              <Label labelText={'Broj podnositelja'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi broj podnositelja'}
                onItemSelect={selected => applicantsNumberFormik.setFieldValue('applicantsNumber', selected)}
                selected={applicantsNumberFormik.values.applicantsNumber}
                labelKey={'value'}
                options={applicantsNumberOptions}
                disabled={sidebarItemPair?.readOnly}
                error={!sidebarItemPair?.readOnly && (applicantsNumberFormik.errors.applicantsNumber as string)}
                showErrorMessage
              />
            </Col>
          </Row>
        </div>
      </FormikProvider>
      {!sidebarItemPair?.readOnly && (
        <>
          <div className={'d-flex justify-content-end pb-4 gap-2'}>
            <div className={'case-copy-from-other-case '}>
              <Input
                placeholder={'Unesite šifru predmeta'}
                maxLength={255}
                addonAppend={
                  isCopyApplicantsPending ? (
                    <FontAwesomeIcon icon="spinner" spin />
                  ) : (
                    <FontAwesomeIcon
                      aria-disabled={
                        (!!copyApplicantsFormik.errors && !!copyApplicantsFormik.errors.caseUuidToCopyFrom) ||
                        copyApplicantsFormik.values.caseUuidToCopyFrom === ''
                      }
                      className={
                        (!!copyApplicantsFormik.errors && !!copyApplicantsFormik.errors.caseUuidToCopyFrom) ||
                        copyApplicantsFormik.values.caseUuidToCopyFrom === ''
                          ? 'disabled'
                          : ''
                      }
                      icon="copy"
                    />
                  )
                }
                value={copyApplicantsFormik.values.caseUuidToCopyFrom}
                onChange={event => {
                  copyApplicantsFormik.setFieldValue('caseUuidToCopyFrom', event.target.value);

                  if (copyApplicantsFormik.submitCount > 0) {
                    copyApplicantsFormik.setErrors({});
                  }
                }}
                addonAppendClick={() => {
                  if (!isCopyApplicantsPending) {
                    copyApplicantsFormik.handleSubmit();
                  }
                }}
                error={
                  copyApplicantsFormik.errors && copyApplicantsFormik.errors.caseUuidToCopyFrom
                    ? copyApplicantsFormik.errors.caseUuidToCopyFrom
                    : ''
                }
              />
            </div>
            <div>
              <Button
                label={'Dodaj podnositelja/opunomoćenika'}
                color={'info'}
                prependIcon={'plus'}
                size={'lg'}
                onClick={openNewApplicantForm}
              />
            </div>
          </div>
        </>
      )}
      <>
        <CaseApplicantTable setApplicant={setCaseApplicantId} setReadOnly={setReadOnly} setSystemRecorded={setSystemRecorded} />
        {caseApplicantId !== null && (
          <>
            {applicantsVariant === CASE_TAB_ENUM.APPLICANTS && (
              <CaseApplicantForm
                caseApplicantId={caseApplicantId}
                setCaseApplicantId={setCaseApplicantId}
                readOnly={readOnly}
                systemRecorded={systemRecorded}
              />
            )}
            {applicantsVariant === CASE_TAB_ENUM.APPLICANTS_V2 && (
              <CaseApplicantFormWithNonMandatoryOib
                caseApplicantId={caseApplicantId}
                setCaseApplicantId={setCaseApplicantId}
                readOnly={readOnly}
                systemRecorded={systemRecorded}
              />
            )}
          </>
        )}
        {!sidebarItemPair?.readOnly && (
          <div className={'case-save-button'}>
            <Button
              label={'Spremi'}
              color={'info'}
              isLoading={applicantsNumberSaving}
              size={'lg'}
              disabled={!applicantsNumberFormik.dirty || !applicantsNumberFormik.isValid}
              onClick={() => applicantsNumberFormik.handleSubmit()}
            />
          </div>
        )}
      </>
    </>
  );
};

export default CaseApplicant;
